/**
 * Live grid bots — API client and types. Mirrors apps/api/src/routes/bots.ts.
 * Bots place REAL orders when the broker connection is live.
 */
import { api } from './api';

export type BotEnv = 'sandbox' | 'live';
export type BotState =
  | 'STARTING'
  | 'ACQUIRING'
  | 'RUNNING'
  | 'STOPPING'
  | 'STOPPED'
  | 'ERROR'
  | 'CASH_STARVED'
  | 'INTERRUPTED';
/**
 * What RUNNING does not tell you: whether the grid is actually turning. Derived
 * by the api from the open orders and the inventory — a read model, never a
 * thing the engine acts on. Null off RUNNING.
 */
export type BotHealth = 'cycling' | 'one-legged' | 'capped' | 'waiting-on-cancel';

export interface BotParams {
  symbol: string;
  lotSize: number;
  spreadPct: number;
  initialLots: number;
  useHoldings: boolean;
  maxInventory: number;
  maxOrders: number;
  stopPct: number;
}

export interface Mark {
  price: number | null;
  bid: number | null;
  ask: number | null;
  last: number | null;
  basis: 'bid' | 'last' | 'none';
  at: number | null;
}

/**
 * Daily delivery batches consumed FIFO, including charges. The average is
 * the remaining cost divided by quantity. CGT has a separate tax ledger.
 */
export interface BotAccountView {
  quantity: number;
  avgCost: number;
  costBasis: number;
  realised: number;
  /**
   * `realised` split by what earned it: grid is quantity bought and sold the
   * same day (repeatable market-making), trend is a sale out of stock carried
   * from a prior day (directional, one-off). They sum to `realised`.
   */
  gridRealised: number;
  trendRealised: number;
  gridCycles: number;
  trendCycles: number;
  /** Net per closed round trip, null until one closes. */
  netPerCycle: number | null;
  unrealised: number | null;
  total: number | null;
  chargesInCost: number;
  chargesRealised: number;
  boughtQuantity: number;
  soldQuantity: number;
  missingQuantity: number;
  issues: string[];
}

export interface BotSummary {
  archivedAt: string | null;
  id: string;
  label: string;
  env: BotEnv;
  symbol: string;
  state: BotState;
  health: BotHealth | null;
  waitingFor: string | null;
  error: string | null;
  params: BotParams;
  reference: number | null;
  firstReference: number | null;
  startInventory: number;
  inventory: number;
  avgCost: number;
  buysFilled: number;
  sellsFilled: number;
  ordersPlaced: number;
  ordersToday: number;
  tradingDay: string;
  pausedDay: string | null;
  /**
   * Realised, closed lots only. `estCost` is our estimate of brokerage, its
   * sales tax, levies and CDC — the broker's note is the truth. No tax here:
   * CGT is netted across the account (see `AccountTax`), never per bot.
   */
  pnl: { gross: number; estCost: number; afterCost: number };
  /**
   * The position as the broker keeps it — weighted average with charges inside
   * the cost. Every screen reads this; the LIFO lot view is behind "Show
   * details" on the bot page and is not the basis of anything.
   */
  account: BotAccountView;
  position: {
    mark: Mark;
    exposure: number;
    marketValue: number | null;
    unrealised: number | null;
    total: number | null;
  };
  createdAt: string;
  stoppedAt: string | null;
  updatedAt: string;
}

export interface BotOrderView {
  id: string;
  role: 'ACQUIRE' | 'BUY' | 'SELL';
  side: 'BUY' | 'SELL';
  price: number;
  quantity: number;
  status: string;
  executed: number;
  avgFillPrice: number;
  brokerOrderId: string;
  exchangeOrderId: string;
  cancelRequested: boolean;
  uncertain: boolean;
  message: string;
  createdAt: string;
  updatedAt: string;
}

export interface OpenLotView {
  lotNo: number;
  source: 'holding' | 'buy';
  quantity: number;
  buyPrice: number;
  cost: number;
  openedAt: string;
  gridSell: number;
  unrealised: number | null;
  unrealisedPct: number | null;
}

export interface ClosedLotView {
  lotNo: number;
  source: 'holding' | 'buy';
  quantity: number;
  buyPrice: number;
  sellPrice: number;
  gross: number;
  /** Opened and closed in one session: brokerage once, and nothing settled. */
  sameDay: boolean;
  openedAt: string;
  closedAt: string;
}

export interface FillView {
  orderId: string;
  side: 'BUY' | 'SELL';
  price: number;
  quantity: number;
  brokerOrderId: string;
  exchangeOrderId: string;
  source: string;
  at: string;
}

export interface BotDetail extends BotSummary {
  resting: BotOrderView[];
  openLots: OpenLotView[];
  closedLots: ClosedLotView[];
  orders: BotOrderView[];
  fills: FillView[];
  events: { at: string; kind: string; message: string }[];
}

export interface EngineHeartbeat {
  at: number;
  loaded: number;
  active: number;
  connections: number;
  alive: boolean;
}

export interface ConnectionStatus {
  configured: boolean;
  liveTradingEnabled: boolean;
  env?: BotEnv;
  state?:
    | 'idle'
    | 'connecting'
    | 'otp_required'
    | 'ready'
    | 'reconnecting'
    | 'refused'
    | 'superseded'
    | 'closed';
  environment?: string;
  otpMessage?: string;
  otpHasEmail?: boolean;
  lastError?: string;
  connectedAt?: number | null;
  account?: { broker: string; client: string } | null;
  accounts?: { broker: string; client: string; status: string }[];
  quotas?: Record<string, unknown> | null;
  sessionStatus?: string;
  unavailable?: string | null;
  budget?: { day: string; used: number; limit: number | null; remaining: number };
}

export interface ConnectionView {
  connections: {
    env: BotEnv;
    tokenHint: string;
    account: string | null;
    selected: boolean;
    updatedAt: string;
  }[];
  status: ConnectionStatus | null;
  engine: EngineHeartbeat | null;
  engineError: string | null;
  secretsConfigured: boolean;
}

export interface AccountView {
  account: { broker: string; client: string } | null;
  settledCash: number;
  buyingPower: number;
  reservedInOpenBuys: number | null;
  holdings: number;
  holdingsCost: number;
  unrealised: number;
  equity: number;
  equityBasis: string;
  today: { bought: number; sold: number } | null;
  positions: { symbol: string; quantity: number; avgPrice: number; marketPrice: number }[];
  snapshotAt: number | null;
  readAt: number;
}

export interface BrokerOrderView {
  brokerOrderId: string;
  exchangeOrderId: string;
  symbol: string;
  side: string;
  type: string;
  status: string;
  price: number;
  quantity: number;
  executed: number;
  fills: { price: number; quantity: number }[];
  message: string;
  at: number | null;
}

export type { Tariff } from '@tradekaro/shared/accounting';
import type { Tariff } from '@tradekaro/shared/accounting';

/** Estimated CGT for the account this month, on NCCPL's basis and netted. */
export interface AccountTax {
  month: string;
  taxableGain: number;
  cgt: number | null;
  issues: string[];
  estimatedTiming: boolean;
  cycles: number;
}

export interface BotDefaults {
  tariff: Tariff;
  stopPct: number;
  extraLots: number;
  liveMaxOrders: number;
  liveTradingEnabled: boolean;
}

export interface NewBotInput {
  symbol: string;
  lotSize: number;
  spreadPct: number;
  initialLots: number;
  useHoldings: boolean;
  maxInventory?: number;
  maxOrders?: number;
  stopPct: number;
  confirmLive?: boolean;
}

export const ACTIVE_STATES: BotState[] = ['STARTING', 'ACQUIRING', 'RUNNING', 'STOPPING'];
export const isWorking = (state: BotState) =>
  ACTIVE_STATES.includes(state) || state === 'INTERRUPTED';

export const listBots = () =>
  api.get<{
    count: number;
    bots: BotSummary[];
    engine: EngineHeartbeat | null;
    tax: AccountTax | null;
  }>('/bots');
export const getBot = (id: string) => api.get<{ bot: BotDetail }>(`/bots/${id}`);
export const getBotDefaults = () => api.get<BotDefaults>('/bots/defaults');
export const createBot = (input: NewBotInput) =>
  api.post<{ bot: BotDetail; warning: string | null }>('/bots', input);
export const stopBot = (id: string) => api.post<{ bot: BotDetail }>(`/bots/${id}/stop`);
export const resumeBot = (id: string, confirmLive: boolean) =>
  api.post<{ bot: BotDetail }>(`/bots/${id}/resume`, { confirmLive });
export const deleteBot = (id: string) => api.del<{ ok: true }>(`/bots/${id}`);

export const getConnection = () => api.get<ConnectionView>('/bots/connection');
/** Write-only: the server stores these encrypted and never returns them. */
export const saveConnection = (body: {
  env: BotEnv;
  token: string;
  pin: string;
  account?: string;
}) => api.put<ConnectionView>('/bots/connection', body);
export const selectConnection = (env: BotEnv) =>
  api.post<ConnectionView>('/bots/connection/select', { env });
export const removeConnection = (env: BotEnv) => api.del<ConnectionView>(`/bots/connection/${env}`);
export const connectBroker = () => api.post<ConnectionView>('/bots/connection/connect');
export const disconnectBroker = () => api.post<ConnectionView>('/bots/connection/disconnect');
export const submitOtp = (code: string) =>
  api.post<ConnectionView>('/bots/connection/otp', { code });
export const getAccount = () => api.get<AccountView>('/bots/account');
export const getBrokerOrders = (fresh = false) =>
  api.get<{ orders: BrokerOrderView[] }>(`/bots/broker-orders${fresh ? '?fresh=1' : ''}`);
export const cancelBrokerOrder = (brokerOrderId: string, exchangeOrderId?: string) =>
  api.post<{ ok: true }>('/bots/broker-orders/cancel', { brokerOrderId, exchangeOrderId });

/**
 * A hand-placed order, out through the same StockIntel session the bots use.
 * MARKET or LIMIT, DAY only — the plan has no GTC and IOC is not offered.
 * Real money: the server gates it on the same allowlist as the bots.
 */
export const placeBrokerOrder = (body: {
  symbol: string;
  side: 'BUY' | 'SELL';
  type: 'MARKET' | 'LIMIT';
  quantity: number;
  price?: number;
}) => api.post<{ requestId: string }>('/bots/orders', body);

// ---------------------------------------------------------------------------
// Cost model — mirrors apps/api/src/services/bots/costs.ts so the form can show
// a break-even as the user types. The server is the authority and refuses a
// grid below it; this is only the preview.
// ---------------------------------------------------------------------------

export { minViableSpreadPct } from '@tradekaro/shared/accounting';
import { minViableSpreadPct } from '@tradekaro/shared/accounting';
export function breakEvenPct(
  price: number,
  quantity: number,
  sameDay: boolean,
  tariff: Tariff,
): number {
  return minViableSpreadPct({ price, quantity, sameDay }, tariff);
}
